<template>
  <div :class="['csat-trust-score', embedded ? 'csat-embedded' : 'mt-4']">
    <v-card v-if="!embedded" class="mb-4">
      <v-card-title class="headline">AI Trust Score &amp; CSAT</v-card-title>
      <v-card-text v-if="loadingConfig" class="text-xs-center pt-4">
        <v-progress-circular indeterminate class="primary--text"></v-progress-circular>
      </v-card-text>
      <v-card-text v-else>
        <v-layout row wrap>
          <v-flex v-show="!hideAnalysisPromptSections" xs12 md6 class="pr-3">
            <v-textarea
              v-model="sourcePrompt"
              label="Prompt source a optimiser"
              auto-grow
              rows="6"
              outline
              hint="Collez ici le prompt de base du bot si vous voulez generer automatiquement le scoring prompt."
              persistent-hint
            ></v-textarea>
          </v-flex>
          <v-flex xs12 md6 class="pl-3">
            <template v-if="!hideAnalysisPromptSections">
            <div class="subheading mb-2">Scenarios CSAT</div>
            <div class="caption grey--text mb-2">
              Chaque scenario garde le meme niveau d'analyse et fait varier uniquement le niveau d'exigence.
            </div>
            <div class="scenario-button-grid mb-3">
              <v-btn
                v-for="scenario in scenarioOptions"
                :key="scenario.id"
                :color="selectedScenarioId === scenario.id ? 'primary' : ''"
                :outline="selectedScenarioId !== scenario.id"
                small
                class="scenario-button"
                @click="applyScenarioById(scenario.id)"
              >
                {{ scenario.label }}
              </v-btn>
            </div>
            <v-text-field
              v-model="saveComment"
              label="Commentaire de version"
              outline
              clearable
            ></v-text-field>
            <div class="tf-gap-wrap mt-2">
              <v-btn
                v-if="draftBackup"
                flat
                color="primary"
                @click="restoreDraftBackup"
              >
                Rollback du brouillon
              </v-btn>
              <v-btn
                color="primary"
                :loading="generatingPrompt"
                :disabled="generatingPrompt || !sourcePrompt.trim().length"
                @click="generateScoringPrompt"
              >
                Generer le scoring prompt
              </v-btn>
              <v-btn
                color="secondary"
                :loading="improvingPrompt"
                :disabled="improvingPrompt"
                @click="improveScoringPrompt"
              >
                Improve
              </v-btn>
              <v-btn
                color="success"
                :loading="savingPrompt"
                :disabled="savingPrompt"
                @click="saveScoringPrompt"
              >
                Sauvegarder
              </v-btn>
            </div>
            <v-alert
              v-if="feedbackMessage"
              :type="feedbackType"
              dismissible
              class="mt-3"
            >
              {{ feedbackMessage }}
            </v-alert>
            <v-card v-if="selectedScenarioDefinition" class="mt-3 pa-3 csat-soft-card">
              <div class="subheading">{{ selectedScenarioDefinition.label }}</div>
              <div class="caption grey--text mt-1">{{ selectedScenarioDefinition.description }}</div>
              <div class="mt-2 body-1">
                <strong>Niveau d'exigence :</strong> {{ selectedScenarioDefinition.strictnessLabel }}
              </div>
            </v-card>
            </template>
          </v-flex>
        </v-layout>

        <v-layout v-show="!hideAnalysisPromptSections" row wrap class="mt-3">
          <v-flex xs12 md6 class="pr-3">
            <v-textarea
              v-model="scoringSections.personality"
              label="Contexte et objectif"
              auto-grow
              rows="6"
              outline
            ></v-textarea>
          </v-flex>
          <v-flex xs12 md6 class="pl-3">
            <v-textarea
              v-model="scoringSections.mission"
              label="Definition du CSAT"
              auto-grow
              rows="6"
              outline
            ></v-textarea>
          </v-flex>
          <v-flex xs12 md6 class="pr-3">
            <v-textarea
              v-model="scoringSections.evaluation_rules"
              label="Regles de traitement"
              auto-grow
              rows="8"
              outline
            ></v-textarea>
          </v-flex>
          <v-flex xs12 md6 class="pl-3">
            <v-textarea
              v-model="scoringSections.output_format"
              label="Format de sortie attendu"
              auto-grow
              rows="8"
              outline
            ></v-textarea>
          </v-flex>
        </v-layout>

        <v-layout row wrap v-if="improvementResult" class="mt-3">
          <v-flex xs12>
            <v-card class="pa-3 csat-soft-card">
              <div class="subheading mb-2">Resultat de l'amelioration IA</div>
              <div class="csat-score-chip">
                Score prompt estime: <strong>{{ improvementResult.score }}/100</strong>
              </div>
              <div class="mt-3">
                <strong>Faiblesses</strong>
                <ul class="csat-list">
                  <li v-for="(item, index) in improvementResult.weaknesses" :key="`weak-${index}`">{{ item }}</li>
                </ul>
              </div>
              <div class="mt-3">
                <strong>Recommandations</strong>
                <ul class="csat-list">
                  <li v-for="(item, index) in improvementResult.recommendations" :key="`rec-${index}`">{{ item }}</li>
                </ul>
              </div>
            </v-card>
          </v-flex>
        </v-layout>

        <v-layout row wrap class="mt-4">
          <v-flex xs12>
            <v-card class="pa-3 csat-soft-card csat-calculate-card">
              <div class="body-1 mb-2">
                L'analyse est réalisée sur les conversations issues de la période sélectionnée
              </div>
              <div class="csat-action-row mt-3">
                <v-btn
                  color="primary"
                  large
                  @click="openAnalysisDialog"
                  v-show="false"
                >
                  Lancer l'analyse
                </v-btn>
                <v-btn
                  color="primary"
                  large
                  :loading="calculating"
                  :disabled="calculating"
                  @click="calculateCsatScore"
                >
                  Lancer l'analyse
                </v-btn>
                <v-btn
                  :color="showAdvanced ? 'primary' : ''"
                  large
                  :outline="!showAdvanced"
                  :dark="showAdvanced"
                  :class="{'csat-toggle-active': showAdvanced}"
                  @click="toggleAdvanced"
                >
                  Avance
                </v-btn>
              </div>
            </v-card>
          </v-flex>
          <v-flex xs12 class="mt-3" v-show="showAdvanced">
            <v-card class="pa-3 csat-soft-card">
              <div class="subheading mb-3">Jeu de donnees</div>
              <v-radio-group v-model="inputType" row>
                <v-radio label="Periode affichee" value="period"></v-radio>
                <v-radio label="IDs de conversations" value="ids"></v-radio>
                <v-radio label="CSV / texte" value="csv"></v-radio>
              </v-radio-group>

              <v-textarea
                v-if="inputType === 'ids'"
                v-model="conversationIdsInput"
                label="IDs de conversations"
                placeholder="Ex: 123>session1, 456>session2"
                auto-grow
                rows="4"
                outline
              ></v-textarea>

              <div v-if="inputType === 'csv'">
                <input
                  ref="csvInput"
                  type="file"
                  accept=".csv,.txt"
                  class="mb-2"
                  @change="readCsvFile"
                >
                <v-textarea
                  v-model="csvRawInput"
                  label="Contenu CSV / texte"
                  placeholder="Collez des ids de conversations separes par virgule, point-virgule, espace ou retour ligne."
                  auto-grow
                  rows="4"
                  outline
                ></v-textarea>
              </div>

              <div class="caption grey--text mt-2">
                Conversations disponibles sur la periode courante: {{ availableConversationCount }}
              </div>
            </v-card>
          </v-flex>

          <v-flex xs12 class="mt-3" v-if="isHistoryView && showInlineHistory">
            <v-card class="pa-3 csat-soft-card">
              <div class="subheading mb-3">Historique</div>
              <wz-histo-chats
                :params="params"
                :numChatsTotal="numChatsTotal"
              ></wz-histo-chats>
            </v-card>
          </v-flex>

          <v-flex xs12 class="mt-3" v-if="isHistoryView">
            <v-card class="pa-3 csat-soft-card">
              <div class="subheading mb-3">Historique</div>
              <v-layout row wrap>
                <v-flex xs12 md6 class="pr-3">
                  <div class="subheading mb-2">Versions du scoring prompt</div>
                  <v-data-table
                    :headers="versionHeaders"
                    :items="promptVersions"
                    class="elevation-1"
                    :rows-per-page-items="[5, 10, 20]"
                  >
                    <template slot="items" slot-scope="props">
                      <tr>
                        <td>{{ formatDateTime(props.item.created_at) }}</td>
                        <td>{{ props.item.created_by }}</td>
                        <td>{{ props.item.scenario_label || '-' }}</td>
                        <td>{{ props.item.comment }}</td>
                        <td class="text-xs-right">
                          <v-btn small flat color="primary" @click="restoreVersion(props.item)">Restaurer</v-btn>
                        </td>
                      </tr>
                    </template>
                  </v-data-table>
                </v-flex>
                <v-flex xs12 md6 class="pl-3">
                  <div class="subheading mb-2">Runs precedents</div>
                  <v-data-table
                    :headers="runHeaders"
                    :items="runHistory"
                    class="elevation-1"
                    :rows-per-page-items="[5, 10, 20]"
                  >
                    <template slot="items" slot-scope="props">
                      <tr>
                        <td>{{ formatDateTime(props.item.created_at) }}</td>
                        <td>{{ props.item.label || props.item.input_type }}</td>
                        <td>{{ props.item.scenario_label || '-' }}</td>
                        <td>{{ props.item.global_score }}</td>
                        <td>{{ props.item.conversation_count }}</td>
                        <td>{{ props.item.error_count }}</td>
                      </tr>
                    </template>
                  </v-data-table>
                </v-flex>
              </v-layout>
            </v-card>
          </v-flex>
        </v-layout>
      </v-card-text>
    </v-card>

    <div v-else>
      <div v-if="loadingConfig" class="text-xs-center pt-4">
        <v-progress-circular indeterminate class="primary--text"></v-progress-circular>
      </div>
      <div v-else class="csat-embedded-body">
        <v-layout row wrap>
          <v-flex xs12 v-if="isLeftView">
            <div class="csat-left-topbar">
              <div class="csat-inline-icon-actions">
                <v-tooltip bottom>
                  <template slot="activator">
                    <v-btn
                      icon
                      small
                      :ripple="false"
                      :flat="!showAdvanced"
                      :color="showAdvanced ? 'accent' : ''"
                      @click="toggleAdvanced"
                    >
                      <v-icon :dark="showAdvanced">tune</v-icon>
                    </v-btn>
                  </template>
                  <span>Avance</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template slot="activator">
                    <v-btn
                      icon
                      small
                      :ripple="false"
                      :flat="!historyActive"
                      :color="historyActive ? 'accent' : ''"
                      @click="toggleHistory"
                    >
                      <v-icon :dark="historyActive">history</v-icon>
                    </v-btn>


                  </template>
                  <span>Historique</span>
                </v-tooltip>
              </div>
            </div>
            <div class="csat-left-action-area">
              <div class="csat-action-row">
                <v-btn
                  color="primary"
                  large
                  @click="openAnalysisDialog"
                  v-show="false"
                >
                  Lancer l'analyse
                </v-btn>
                <v-tooltip bottom>
                  <template slot="activator">
                    <v-btn
                      color="primary"
                      large
                      :loading="calculating"
                      :disabled="calculating"
                      @click="calculateCsatScore"
                    >
                      Lancer l'analyse
                    </v-btn>
                  </template>
                  <span>L'analyse est realisee sur les conversations issues de la periode selectionnee</span>
                </v-tooltip>
              </div>
            </div>
          </v-flex>

          <v-flex xs12 class="mt-3" v-if="isLeftView" v-show="showAdvanced">
            <v-card class="pa-3 csat-soft-card">
              <div class="subheading mb-3">Jeu de donnees</div>
              <v-radio-group v-model="inputType" row>
                <v-radio label="Periode affichee" value="period"></v-radio>
                <v-radio label="IDs de conversations" value="ids"></v-radio>
                <v-radio label="CSV / texte" value="csv"></v-radio>
              </v-radio-group>

              <v-textarea
                v-if="inputType === 'ids'"
                v-model="conversationIdsInput"
                label="IDs de conversations"
                placeholder="Ex: 123>session1, 456>session2"
                auto-grow
                rows="4"
                outline
              ></v-textarea>

              <div v-if="inputType === 'csv'">
                <input
                  ref="csvInput"
                  type="file"
                  accept=".csv,.txt"
                  class="mb-2"
                  @change="readCsvFile"
                >
                <v-textarea
                  v-model="csvRawInput"
                  label="Contenu CSV / texte"
                  placeholder="Collez des ids de conversations separes par virgule, point-virgule, espace ou retour ligne."
                  auto-grow
                  rows="4"
                  outline
                ></v-textarea>
              </div>

              <div class="caption grey--text mt-2">
                Conversations disponibles sur la periode courante: {{ availableConversationCount }}
              </div>
            </v-card>
          </v-flex>

          <v-flex xs12 v-if="isHistoryView && showInlineHistory">
            <v-card class="pa-3 csat-soft-card">
              <div class="subheading mb-3">Historique</div>
              <wz-histo-chats
                :params="params"
                :numChatsTotal="numChatsTotal"
              ></wz-histo-chats>
            </v-card>
          </v-flex>

          <v-flex xs12  v-if="isHistoryView">
            <v-card class="pa-3 csat-soft-card">
              <div class="subheading font-weight-bold grey--text">Historique</div>

              <v-expansion-panel>
                <v-expansion-panel-content>
                  <template v-slot:header>
                    <div>Versions du scoring prompt</div>
                  </template>
                  <v-data-table
                    :headers="versionHeaders"
                    :items="promptVersions"
                    class="elevation-1"
                    :rows-per-page-items="[5, 10, 20]"
                  >
                    <template slot="items" slot-scope="props">
                      <tr>
                        <td>{{ formatDateTime(props.item.created_at) }}</td>
                        <td>{{ props.item.created_by }}</td>
                        <td>{{ props.item.scenario_label || '-' }}</td>
                        <td>{{ props.item.comment }}</td>
                        <td class="text-xs-right">
                          <v-btn small flat color="primary" @click="restoreVersion(props.item)">Restaurer</v-btn>
                        </td>
                      </tr>
                    </template>
                  </v-data-table>
                </v-expansion-panel-content>

                <v-expansion-panel-content>
                  <template v-slot:header>
                    <div>Runs precedents</div>
                  </template>
                  <v-data-table
                    :headers="runHeaders"
                    :items="runHistory"
                    class="elevation-1"
                    :rows-per-page-items="[5, 10, 20]"
                  >
                    <template slot="items" slot-scope="props">
                      <tr>
                        <td>{{ formatDateTime(props.item.created_at) }}</td>
                        <td>{{ props.item.label || props.item.input_type }}</td>
                        <td>{{ props.item.scenario_label || '-' }}</td>
                        <td>{{ props.item.global_score }}</td>
                        <td>{{ props.item.conversation_count }}</td>
                        <td>{{ props.item.error_count }}</td>
                      </tr>
                    </template>
                  </v-data-table>
                </v-expansion-panel-content>
              </v-expansion-panel>

            </v-card>
          </v-flex>
        </v-layout>
      </div>
    </div>

    <v-dialog
      v-model="showAnalysisDialog"
      max-width="1100"
      content-class="csat-analysis-dialog-wrapper"
      persistent
    >
      <v-card class="csat-analysis-dialog">
        <div class="csat-dialog-header primary">
          <v-btn icon @click="closeAnalysisDialog">
            <v-icon class="white--text">close</v-icon>
          </v-btn>
          <div class="csat-dialog-title white--text">Analyse CSAT guidee</div>
        </div>
        <div class="csat-stepper-top">
          <v-stepper v-model="analysisDialogStep" non-linear flat>
            <v-stepper-header>
              <v-stepper-step :complete="analysisDialogStep > 1" step="1" @click="setAnalysisDialogStep(1)">
                Prompt du bot
              </v-stepper-step>
              <v-divider></v-divider>
              <v-stepper-step :complete="analysisDialogStep > 2" step="2" @click="setAnalysisDialogStep(2)">
                Prompt CSAT
              </v-stepper-step>
              <v-divider></v-divider>
              <v-stepper-step :complete="analysisDialogStep > 3" step="3" @click="setAnalysisDialogStep(3)">
                Calculate
              </v-stepper-step>
              <v-divider></v-divider>
              <v-stepper-step :complete="analysisDialogStep > 4" step="4" @click="setAnalysisDialogStep(4)">
                Resultats
              </v-stepper-step>
              <v-divider></v-divider>
              <v-stepper-step :complete="analysisDialogStep > 5" step="5" @click="setAnalysisDialogStep(5)">
                Recommandation
              </v-stepper-step>
            </v-stepper-header>
          </v-stepper>
        </div>
        <v-card-text class="pt-4 csat-dialog-content">
          <v-progress-linear
            v-if="analysisDialogLoading"
            indeterminate
            color="primary"
            class="mb-4"
          ></v-progress-linear>

          <div v-show="analysisDialogStep === 1" class="csat-step-pane csat-step-pane-bot">
            <div class="subheading mb-3">Prompt du chatbot actuellement utilise</div>
            <div class="csat-step-pane-body">
              <textarea
                :value="analysisDialogBotPrompt"
                readonly
                class="csat-native-textarea"
              ></textarea>
            </div>
          </div>

          <div v-show="analysisDialogStep === 2" class="csat-step-pane">
            <div class="subheading mb-3">Prompts dedies au calcul du CSAT</div>
            <v-layout row wrap>
              <v-flex xs12 md6 class="pr-3">
                <v-textarea
                  :value="analysisDialogCsatSections.personality"
                  label="Contexte et objectif"
                  auto-grow
                  rows="5"
                  outline
                  readonly
                  class="csat-dialog-textarea"
                ></v-textarea>
              </v-flex>
              <v-flex xs12 md6 class="pl-3">
                <v-textarea
                  :value="analysisDialogCsatSections.mission"
                  label="Definition du CSAT"
                  auto-grow
                  rows="5"
                  outline
                  readonly
                  class="csat-dialog-textarea"
                ></v-textarea>
              </v-flex>
              <v-flex xs12 md6 class="pr-3">
                <v-textarea
                  :value="analysisDialogCsatSections.evaluation_rules"
                  label="Regles de traitement"
                  auto-grow
                  rows="5"
                  outline
                  readonly
                  class="csat-dialog-textarea"
                ></v-textarea>
              </v-flex>
              <v-flex xs12 md6 class="pl-3">
                <v-textarea
                  :value="analysisDialogCsatSections.output_format"
                  label="Format de sortie attendu"
                  auto-grow
                  rows="5"
                  outline
                  readonly
                  class="csat-dialog-textarea"
                ></v-textarea>
              </v-flex>
            </v-layout>
          </div>

          <div v-show="analysisDialogStep === 3" class="csat-step-pane">
            <div class="subheading mb-3">Calculate</div>
            <div class="csat-step-loader">
              <v-progress-circular indeterminate color="primary" :size="64" :width="4"></v-progress-circular>
              <div class="mt-3 body-1 grey--text text--darken-1">Calcul du score CSAT en cours...</div>
            </div>
          </div>

          <div v-show="analysisDialogStep === 4" class="csat-step-pane csat-step-pane-results">
            <div class="subheading mb-3">Resultats</div>
            <v-layout v-if="analysisDialogResultSummary" row wrap class="ma-0">
              <v-flex xs12 md4 class="pa-0 pr-3">
                <v-card class="pa-4 text-xs-center csat-soft-card csat-score-card">
                  <div class="caption grey--text">AI CSAT Score</div>
                  <div class="display-2 primary--text font-weight-bold">{{ analysisDialogResultSummary.global_score }}</div>
                  <div class="caption grey--text">/100</div>
                  <div class="mt-3" v-if="analysisDialogPreviousSummary">
                    <span class="caption grey--text">Periode precedente: </span>
                    <strong>{{ analysisDialogPreviousSummary.global_score }}</strong>
                  </div>
                  <div v-if="analysisDialogPreviousSummary" :class="analysisDialogDeltaClass" class="mt-1 font-weight-bold">
                    {{ analysisDialogFormattedDelta }}
                  </div>
                </v-card>
              </v-flex>
              <v-flex xs12 md8 class="pa-0">
                <v-card class="pa-4 csat-soft-card csat-result-details-card">
                  <div>
                    Conversations scorees : <strong>{{ analysisDialogResultSummary.conversation_count }}</strong></br>
                    Erreurs non bloquantes : <strong>{{ analysisDialogResultSummary.error_count }}</strong>
                  </div>
                  <div class="mt-3">
                    <strong>Insights</strong>
                    <ul class="csat-list">
                      <li v-for="(item, index) in analysisDialogResultSummary.insights" :key="`dialog-insight-${index}`">{{ item }}</li>
                    </ul>
                  </div>
                  <div class="mt-3">
                    <strong>Recommandations</strong>
                    <ul class="csat-list">
                      <li v-for="(item, index) in analysisDialogResultSummary.recommendations" :key="`dialog-rec-${index}`">{{ item }}</li>
                    </ul>
                  </div>
                </v-card>
              </v-flex>
            </v-layout>
            <v-alert
              v-else-if="analysisDialogResultError"
              type="error"
              value="true"
            >
              {{ analysisDialogResultError }}
            </v-alert>
          </div>

          <div v-show="analysisDialogStep === 5" class="csat-step-pane csat-step-pane-results">
            <div class="subheading mb-3">Recommandation</div>
            <v-layout row wrap class="ma-0">
              <v-flex xs12 md6 class="pa-0 pr-3">
                <div class="subheading mb-2">Runs precedents</div>
                <v-data-table
                  :headers="runHeaders"
                  :items="runHistory"
                  class="elevation-1"
                  :rows-per-page-items="[5, 10, 20]"
                >
                  <template slot="items" slot-scope="props">
                    <tr>
                      <td>{{ formatDateTime(props.item.created_at) }}</td>
                      <td>{{ props.item.label || props.item.input_type }}</td>
                      <td>{{ props.item.scenario_label || '-' }}</td>
                      <td>{{ props.item.global_score }}</td>
                      <td>{{ props.item.conversation_count }}</td>
                      <td>{{ props.item.error_count }}</td>
                    </tr>
                  </template>
                </v-data-table>
              </v-flex>
              <v-flex xs12 md6 class="pa-0">
                <v-card class="pa-4 csat-soft-card csat-result-details-card">
                  <div class="subheading mb-3">Que veux-tu ameliorer ?</div>
                  <div class="body-1 mb-3">
                    A partir du score obtenu, tu peux soit retravailler le prompt CSAT de notation, soit retravailler le prompt systeme du chatbot.
                  </div>
                  <div class="tf-gap-wrap">
                    <v-btn color="primary" @click="setAnalysisDialogStep(2)">
                      Ameliorer le prompt CSAT
                    </v-btn>
                    <v-btn color="primary" outline @click="goToBotbuilderPromptSystem">
                      Ameliorer le prompt du chatbot
                    </v-btn>
                  </div>
                </v-card>
              </v-flex>
            </v-layout>
          </div>
        </v-card-text>

        <v-card-actions class="csat-dialog-actions">
          <v-btn
            flat
            @click="closeAnalysisDialog"
          >
            Fermer
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            flat
            :disabled="analysisDialogStep === 1 || analysisDialogCalculating"
            @click="goToPreviousAnalysisStep"
          >
            Precedent
          </v-btn>
          <v-btn
            color="primary"
            :disabled="analysisDialogStep >= analysisDialogMaxStep || analysisDialogCalculating"
            @click="goToNextAnalysisStep"
          >
            Suivant
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-card class="mb-4" v-if="!embedded && resultSummary">
      <v-card-title class="headline">Resultats</v-card-title>
      <v-card-text>
        <v-layout row wrap>
          <v-flex xs12 md4 class="pr-3">
            <v-card class="pa-4 text-xs-center csat-soft-card csat-score-card">
              <div class="caption grey--text">AI CSAT Score</div>
              <div class="display-2 primary--text font-weight-bold">{{ resultSummary.global_score }}</div>
              <div class="caption grey--text">/100</div>
              <div class="mt-3" v-if="previousSummary">
                <span class="caption grey--text">Periode precedente: </span>
                <strong>{{ previousSummary.global_score }}</strong>
              </div>
              <div v-if="previousSummary" :class="deltaClass" class="mt-1 font-weight-bold">
                {{ formattedDelta }}
              </div>
            </v-card>
          </v-flex>
          <v-flex xs12 md8 class="pl-3">
            <v-card class="pa-4 csat-soft-card">
              <div><strong>Conversations scorees:</strong> {{ resultSummary.conversation_count }}</div>
              <div><strong>Erreurs non bloquantes:</strong> {{ resultSummary.error_count }}</div>
              <div class="mt-3">
                <strong>Insights</strong>
                <ul class="csat-list">
                  <li v-for="(item, index) in resultSummary.insights" :key="`insight-${index}`">{{ item }}</li>
                </ul>
              </div>
              <div class="mt-3">
                <strong>Recommandations</strong>
                <ul class="csat-list">
                  <li v-for="(item, index) in resultSummary.recommendations" :key="`srec-${index}`">{{ item }}</li>
                </ul>
              </div>
            </v-card>
          </v-flex>
        </v-layout>

        <v-layout row wrap class="mt-4">
          <v-flex xs12 md8 class="pr-3">
            <v-data-table
              :headers="resultHeaders"
              :items="resultRows"
              class="elevation-1"
              :rows-per-page-items="[10, 25, 50]"
            >
              <template slot="items" slot-scope="props">
                <tr>
                  <td>{{ props.item.conversation_id }}</td>
                  <td>{{ props.item.visitor_id }}</td>
                  <td><strong>{{ props.item.score }}</strong></td>
                  <td>{{ props.item.label }}</td>
                  <td>{{ props.item.summary }}</td>
                </tr>
              </template>
            </v-data-table>
          </v-flex>
          <v-flex xs12 md4 class="pl-3">
            <v-card class="pa-3 csat-soft-card" v-if="resultErrors.length">
              <div class="subheading mb-2">Logs d'erreurs</div>
              <ul class="csat-list">
                <li v-for="(item, index) in resultErrors" :key="`err-${index}`">
                  {{ item.conversation_id || 'conversation inconnue' }} - {{ item.error }}
                </li>
              </ul>
            </v-card>
          </v-flex>
        </v-layout>
      </v-card-text>
    </v-card>


  </div>
</template>

<script>
import moment from 'moment'
import WzHistoChats from '../watcheezy/WzHistoChats'

export default {
  components: {
    WzHistoChats
  },
  props: {
    view: {
      type: String,
      default: 'full' // full | left | history
    },
    embedded: {
      type: Boolean,
      default: false
    },
    visitors: {
      type: Array,
      default: () => []
    },
    params: {
      type: String,
      default: ''
    },
    numChatsTotal: {
      type: String,
      default: '0'
    },
    historyActive: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      hideAnalysisPromptSections: true,
      loadingConfig: false,
      savingPrompt: false,
      generatingPrompt: false,
      improvingPrompt: false,
      calculating: false,
      showAdvanced: false,
      showAnalysisDialog: false,
      analysisDialogLoading: false,
      analysisDialogCalculating: false,
      analysisDialogStep: 1,
      analysisDialogMaxStep: 5,
      analysisDialogBotPrompt: '',
      analysisDialogResultSummary: null,
      analysisDialogPreviousSummary: null,
      analysisDialogResultError: '',
      analysisDialogCsatSections: {
        personality: '',
        mission: '',
        evaluation_rules: '',
        output_format: ''
      },
      sourcePrompt: '',
      saveComment: '',
      selectedScenarioId: 'balanced',
      inputType: 'period',
      conversationIdsInput: '',
      csvRawInput: '',
      draftBackup: null,
      scoringSections: {
        personality: '',
        mission: '',
        evaluation_rules: '',
        output_format: ''
      },
      promptVersions: [],
      runHistory: [],
      improvementResult: null,
      resultSummary: null,
      previousSummary: null,
      resultRows: [],
      resultErrors: [],
      lastRunPayload: null,
      feedbackMessage: '',
      feedbackType: 'success',
      versionHeaders: [
        { text: 'Date', value: 'created_at' },
        { text: 'Auteur', value: 'created_by' },
        { text: 'Scenario', value: 'scenario_label' },
        { text: 'Commentaire', value: 'comment' },
        { text: '', value: 'actions', sortable: false }
      ],
      runHeaders: [
        { text: 'Date', value: 'created_at' },
        { text: 'Source', value: 'label' },
        { text: 'Scenario', value: 'scenario_label' },
        { text: 'Score', value: 'global_score' },
        { text: 'Conversations', value: 'conversation_count' },
        { text: 'Erreurs', value: 'error_count' }
      ],
      resultHeaders: [
        { text: 'Conversation', value: 'conversation_id' },
        { text: 'Visiteur', value: 'visitor_id' },
        { text: 'Score', value: 'score' },
        { text: 'Label', value: 'label' },
        { text: 'Resume', value: 'summary' }
      ],
      scenarioPresets: [
        {
          id: 'balanced',
          label: 'Equilibre',
          strictnessLabel: 'Standard',
          description: 'Scenario de reference. Il valorise la resolution et la clarte, tout en laissant une marge raisonnable quand le service rendu est globalement utile.',
          personalityTone: 'Tu es un evaluateur CSAT equilibre, exigeant mais juste. Tu dois conserver une lecture complete de la conversation, sans sur-penaliser les micro-frictions si l\'objectif final est atteint de facon claire.',
          missionTone: 'Mesurer la satisfaction probable avec un niveau d\'exigence standard, en evaluant la resolution, la clarte, la pertinence, la fluidite et la perception globale du visiteur.',
          rulesAppend: '- Scenario Equilibre : un chatbot peut rester bien note si la demande est correctement resolue, meme si la conversation n\'est pas parfaite.\n- Penaliser modereement les frictions mineures quand elles ne remettent pas en cause la resolution.'
        },
        {
          id: 'demanding',
          label: 'Exigeant',
          strictnessLabel: 'Eleve',
          description: 'Meme niveau d analyse, mais la note baisse plus vite si le bot manque de precision, oblige le visiteur a se repeter ou laisse de l\'ambiguite.',
          personalityTone: 'Tu es un evaluateur CSAT exigeant, precis et rigoureux. Tu gardes la meme profondeur d\'analyse, mais tu consideres qu\'une bonne experience doit etre claire, fluide et limiter les efforts inutiles du visiteur.',
          missionTone: 'Mesurer la satisfaction probable avec un niveau d\'exigence eleve, en mettant davantage l\'accent sur l\'absence d\'ambiguite, la fluidite, l\'autonomie offerte au visiteur et la qualite de la resolution.',
          rulesAppend: '- Scenario Exigeant : une reponse seulement partielle ou imprécise doit etre nettement plus penalisee.\n- Si le visiteur doit reformuler, patienter ou faire un effort notable pour obtenir une reponse, la note doit baisser de facon visible.'
        },
        {
          id: 'strict',
          label: 'Tres exigeant',
          strictnessLabel: 'Tres eleve',
          description: 'Utilise ce scenario pour challenger fortement le bot. Seules les conversations tres bien resolues, fluides et rassurantes meritent les meilleures notes.',
          personalityTone: 'Tu es un evaluateur CSAT tres exigeant, centre sur l\'excellence de service. Tu gardes la meme profondeur d\'analyse, mais tu reserves les notes hautes aux conversations reellement exemplaires.',
          missionTone: 'Mesurer la satisfaction probable avec un niveau d\'exigence tres eleve. Une experience seulement correcte ne doit pas obtenir une note premium.',
          rulesAppend: '- Scenario Tres exigeant : les notes superieures a 85 sont reservees aux conversations claires, rapides, completes et sans zone de doute importante.\n- Toute incomprehension, approximation ou transfert implicite de charge vers le visiteur doit entrainer une baisse sensible de la note.'
        },
        {
          id: 'zero_tolerance',
          label: 'Zero tolerance',
          strictnessLabel: 'Maximum',
          description: 'Scenario de stress test. Il sert a detecter les conversations ou la moindre friction importante empêche une experience vraiment satisfaisante.',
          personalityTone: 'Tu es un evaluateur CSAT zero tolerance pour les frictions importantes. Tu conserves la meme lecture detaillee, mais tu appliques un niveau d\'exigence maximal sur la precision, la resolution et l\'effort demande au visiteur.',
          missionTone: 'Mesurer la satisfaction probable avec un niveau d\'exigence maximal afin d\'identifier tout point de faiblesse qui empêche une experience haut de gamme.',
          rulesAppend: '- Scenario Zero tolerance : la moindre reponse inexacte, evasive, incomplete ou generatrice de frustration doit faire chuter fortement la note.\n- Une note haute n\'est possible que si la conversation est exemplaire de bout en bout, sans effort notable du visiteur.'
        }
      ]
    }
  },
  computed: {
    siteKey () {
      return this.getSiteKey()
    },
    scenarioOptions () {
      return this.scenarioPresets
    },
    selectedScenarioDefinition () {
      return this.scenarioPresets.find(item => item.id === this.selectedScenarioId) || this.scenarioPresets[0]
    },
    availableConversationCount () {
      return this.flattenConversations(this.visitors).length
    },
    showInlineHistory () {
      const value = (this.numChatsTotal || '').toString().trim()
      return value !== '' && value !== '0'
    },
    isLeftView () {
      return this.view === 'left' || this.view === 'full'
    },
    isHistoryView () {
      return this.view === 'history'
    },
    deltaValue () {
      if (!this.resultSummary || !this.previousSummary) return null
      return Number((this.resultSummary.global_score - this.previousSummary.global_score).toFixed(1))
    },
    formattedDelta () {
      if (this.deltaValue === null) return ''
      const prefix = this.deltaValue > 0 ? '+' : ''
      return prefix + this.deltaValue + ' pts'
    },
    deltaClass () {
      if (this.deltaValue === null) return ''
      if (this.deltaValue > 0) return 'success--text'
      if (this.deltaValue < 0) return 'error--text'
      return 'grey--text'
    },
    analysisDialogDeltaValue () {
      if (!this.analysisDialogResultSummary || !this.analysisDialogPreviousSummary) return null
      return Number((this.analysisDialogResultSummary.global_score - this.analysisDialogPreviousSummary.global_score).toFixed(1))
    },
    analysisDialogFormattedDelta () {
      if (this.analysisDialogDeltaValue === null) return ''
      const prefix = this.analysisDialogDeltaValue > 0 ? '+' : ''
      return prefix + this.analysisDialogDeltaValue + ' pts'
    },
    analysisDialogDeltaClass () {
      if (this.analysisDialogDeltaValue === null) return ''
      if (this.analysisDialogDeltaValue > 0) return 'success--text'
      if (this.analysisDialogDeltaValue < 0) return 'error--text'
      return 'grey--text'
    }
  },
  created () {
    this.loadConfig()
  },
  watch: {
    siteKey () {
      this.loadConfig()
    },
    analysisDialogStep (value) {
      if (this.showAnalysisDialog && value === 3) {
        this.runAnalysisDialogCalculation()
      }
    }
  },
  methods: {
    getSiteKey () {
      if (this.$store.state._Sites && this.$store.state._Sites.siteSelected && this.$store.state._Sites.siteSelected.LIC_KEY) {
        return this.$store.state._Sites.siteSelected.LIC_KEY
      }
      if (this.$store.state._User && this.$store.state._User.licAllKeys && this.$store.state._User.licAllKeys.length) {
        return this.$store.state._User.licAllKeys[0]
      }
      return ''
    },
    currentUserName () {
      const user = this.$store.state._User || {}
      return [user.fname, user.lname].filter(Boolean).join(' ').trim()
    },
    currentBotUserId () {
      const siteSelected = this.$store.state._Sites && this.$store.state._Sites.siteSelected
      if (!siteSelected || !siteSelected.DSK_BOT_USER) return ''
      try {
        const botUser = JSON.parse(siteSelected.DSK_BOT_USER)
        return botUser && botUser.user_id ? botUser.user_id : ''
      } catch (e) {
        return ''
      }
    },
    setFeedback (message, type) {
      this.feedbackMessage = message
      this.feedbackType = type || 'success'
    },
    toggleAdvanced () {
      this.showAdvanced = !this.showAdvanced
    },
    toggleHistory () {
      this.$emit('toggle-history')
    },
    async openAnalysisDialog () {
      this.showAnalysisDialog = true
      this.analysisDialogStep = 1
      this.analysisDialogLoading = true
      this.analysisDialogCalculating = false
      this.analysisDialogBotPrompt = ''
      this.analysisDialogResultSummary = null
      this.analysisDialogPreviousSummary = null
      this.analysisDialogResultError = ''
      this.analysisDialogCsatSections = {
        personality: this.scoringSections.personality || '',
        mission: this.scoringSections.mission || '',
        evaluation_rules: this.scoringSections.evaluation_rules || '',
        output_format: this.scoringSections.output_format || ''
      }

      try {
        await Promise.all([
          this.loadAnalysisDialogBotPrompt(),
          this.loadAnalysisDialogCsatPrompt()
        ])
      } catch (err) {
        this.setFeedback('Certaines donnees de la fenetre d analyse n ont pas pu etre chargees.', 'warning')
      } finally {
        this.analysisDialogLoading = false
      }
    },
    closeAnalysisDialog () {
      this.showAnalysisDialog = false
      this.analysisDialogStep = 1
      this.analysisDialogCalculating = false
    },
    setAnalysisDialogStep (step) {
      this.analysisDialogStep = step
    },
    goToNextAnalysisStep () {
      if (this.analysisDialogStep < this.analysisDialogMaxStep) {
        this.analysisDialogStep += 1
      }
    },
    goToPreviousAnalysisStep () {
      if (this.analysisDialogStep > 1) {
        this.analysisDialogStep -= 1
      }
    },
    goToBotbuilderPromptSystem () {
      this.closeAnalysisDialog()
      this.$router.push({ path: '/botbuilder/openAIConfig', query: { focus: 'prompt-system' } })
    },
    async loadAnalysisDialogBotPrompt () {
      const siteKey = this.getSiteKey()
      const userID = this.currentBotUserId()
      if (!siteKey || !userID) return
      const response = await this.axios.get('get/openAI_prompt.php?userID=' + encodeURIComponent(userID) + '&sitekey=' + encodeURIComponent(siteKey))
      this.analysisDialogBotPrompt = response.data && response.data.prompt ? response.data.prompt : ''
    },
    async loadAnalysisDialogCsatPrompt () {
      const siteKey = this.getSiteKey()
      const userID = this.currentBotUserId()
      if (!siteKey) return
      const query = 'get/openAI_prompt_csat.php?sitekey=' + encodeURIComponent(siteKey) + (userID ? '&userID=' + encodeURIComponent(userID) : '')
      const response = await this.axios.get(query)
      const data = response.data || {}
      this.analysisDialogCsatSections = {
        personality: data.prompt_csat_personality || '',
        mission: data.prompt_csat_mission || '',
        evaluation_rules: data.prompt_csat_rules || '',
        output_format: data.prompt_csat_block4 || ''
      }
    },
    async runAnalysisDialogCalculation () {
      if (this.analysisDialogCalculating) return
      const conversations = this.getSelectedConversations()
      if (!conversations.length) {
        this.analysisDialogResultSummary = null
        this.analysisDialogPreviousSummary = null
        this.analysisDialogResultError = 'Aucune conversation valide a scorer avec la selection courante.'
        this.analysisDialogStep = 4
        return
      }

      this.analysisDialogCalculating = true
      this.analysisDialogResultSummary = null
      this.analysisDialogPreviousSummary = null
      this.analysisDialogResultError = ''

      try {
        const label = this.inputType === 'period' ? 'periode courante' : 'echantillon manuel'
        const currentResponse = await this.requestEvaluation(conversations, label)
        this.analysisDialogResultSummary = currentResponse.summary || null

        if (currentResponse.run) {
          this.runHistory.unshift(currentResponse.run)
          this.runHistory = this.runHistory.slice(0, 20)
        }

        if (this.inputType === 'period') {
          const previousConversations = await this.fetchPreviousPeriodConversations()
          if (previousConversations.length) {
            const previousResponse = await this.requestEvaluation(previousConversations, 'periode precedente')
            this.analysisDialogPreviousSummary = previousResponse.summary || null
          }
        }
        this.analysisDialogStep = 4
      } catch (err) {
        this.analysisDialogResultError = 'Le calcul CSAT a echoue.'
        this.analysisDialogStep = 4
      } finally {
        this.analysisDialogCalculating = false
      }
    },
    async loadConfig () {
      const siteKey = this.getSiteKey()
      if (!siteKey) return
      this.loadingConfig = true
      try {
        const response = await this.axios.get('get/csat_config.php?sitekey=' + encodeURIComponent(siteKey))
        const data = response.data || {}
        this.sourcePrompt = data.source_prompt || ''
        this.scoringSections = Object.assign({}, this.scoringSections, (data.scoring_prompt && data.scoring_prompt.sections) ? data.scoring_prompt.sections : {})
        this.selectedScenarioId = (data.scoring_prompt && data.scoring_prompt.scenario_id) ? data.scoring_prompt.scenario_id : 'balanced'
        this.promptVersions = Array.isArray(data.scoring_prompt_versions) ? data.scoring_prompt_versions : []
        this.runHistory = Array.isArray(data.runs) ? data.runs : []
      } catch (err) {
        this.setFeedback('Impossible de charger la configuration CSAT.', 'error')
      } finally {
        this.loadingConfig = false
      }
    },
    async saveScoringPrompt () {
      const siteKey = this.getSiteKey()
      if (!siteKey) return
      this.savingPrompt = true
      try {
        const payload = new URLSearchParams()
        payload.append('sitekey', siteKey)
        payload.append('source_prompt', this.sourcePrompt)
        payload.append('sections', JSON.stringify(this.scoringSections))
        payload.append('comment', this.saveComment)
        payload.append('user_name', this.currentUserName())
        payload.append('scenario_id', this.selectedScenarioId)
        payload.append('scenario_label', this.selectedScenarioDefinition ? this.selectedScenarioDefinition.label : 'Equilibre')
        const response = await this.axios.post('post/csat_config.php', payload, {headers: {'Content-Type': 'application/x-www-form-urlencoded'}})
        const data = response.data || {}
        this.promptVersions = Array.isArray(data.config.scoring_prompt_versions) ? data.config.scoring_prompt_versions : []
        this.runHistory = Array.isArray(data.config.runs) ? data.config.runs : []
        this.saveComment = ''
        this.setFeedback('Scoring prompt sauvegarde avec nouvelle version.', 'success')
      } catch (err) {
        this.setFeedback('Erreur pendant la sauvegarde du scoring prompt.', 'error')
      } finally {
        this.savingPrompt = false
      }
    },
    async generateScoringPrompt () {
      this.generatingPrompt = true
      try {
        this.captureDraftBackup('before_generate')
        const payload = new URLSearchParams()
        payload.append('source_prompt', this.sourcePrompt)
        const response = await this.axios.post('post/csat_generate_prompt.php', payload, {headers: {'Content-Type': 'application/x-www-form-urlencoded'}})
        this.scoringSections = Object.assign({}, this.scoringSections, response.data.sections || {})
        this.setFeedback('Scoring prompt genere automatiquement.', 'success')
      } catch (err) {
        this.setFeedback('Generation du scoring prompt impossible.', 'error')
      } finally {
        this.generatingPrompt = false
      }
    },
    async improveScoringPrompt () {
      this.improvingPrompt = true
      try {
        this.captureDraftBackup('before_improve')
        const payload = new URLSearchParams()
        payload.append('source_prompt', this.sourcePrompt)
        payload.append('sections', JSON.stringify(this.scoringSections))
        const response = await this.axios.post('post/csat_improve_scoring_prompt.php', payload, {headers: {'Content-Type': 'application/x-www-form-urlencoded'}})
        const data = response.data || {}
        this.improvementResult = data
        if (data.improved_sections) {
          this.scoringSections = Object.assign({}, this.scoringSections, data.improved_sections)
        }
        this.setFeedback('Prompt ameliore. Vous pouvez encore l editer avant sauvegarde.', 'success')
      } catch (err) {
        this.setFeedback('Amelioration IA indisponible.', 'error')
      } finally {
        this.improvingPrompt = false
      }
    },
    restoreVersion (version) {
      if (!version || !version.sections) return
      this.captureDraftBackup('rollback_avant_restauration')
      this.scoringSections = Object.assign({}, this.scoringSections, version.sections)
      if (version.source_prompt) {
        this.sourcePrompt = version.source_prompt
      }
      if (version.scenario_id) {
        this.selectedScenarioId = version.scenario_id
      }
      this.setFeedback('Version restauree dans l editeur. Pensez a sauvegarder si vous voulez la rendre active.', 'success')
    },
    captureDraftBackup (reason) {
      this.draftBackup = {
        reason: reason || 'manual',
        savedAt: new Date().toISOString(),
        sourcePrompt: this.sourcePrompt,
        selectedScenarioId: this.selectedScenarioId,
        scoringSections: Object.assign({}, this.scoringSections)
      }
    },
    restoreDraftBackup () {
      if (!this.draftBackup) return
      this.sourcePrompt = this.draftBackup.sourcePrompt || ''
      this.selectedScenarioId = this.draftBackup.selectedScenarioId || 'balanced'
      this.scoringSections = Object.assign({}, this.scoringSections, this.draftBackup.scoringSections || {})
      this.setFeedback('Rollback du brouillon effectue. Vous avez retrouve l etat precedent de l editeur.', 'success')
    },
    applyScenarioPreset () {
      if (!this.selectedScenarioDefinition) return
      this.captureDraftBackup('before_apply_scenario')
      const preset = this.selectedScenarioDefinition
      const basePersonality = this.scoringSections.personality || ''
      const baseMission = this.scoringSections.mission || ''
      const baseRules = this.scoringSections.evaluation_rules || ''

      this.scoringSections = Object.assign({}, this.scoringSections, {
        personality: this.mergeScenarioBlock(basePersonality, preset.personalityTone, 'scenario_personality'),
        mission: this.mergeScenarioBlock(baseMission, preset.missionTone, 'scenario_mission'),
        evaluation_rules: this.mergeScenarioBlock(baseRules, preset.rulesAppend, 'scenario_rules')
      })

      this.setFeedback('Scenario applique. Le meme niveau d analyse est conserve, avec un niveau d exigence ajuste. Vous pouvez rollback si besoin.', 'success')
    },
    applyScenarioById (scenarioId) {
      if (!scenarioId) return
      if (this.selectedScenarioId === scenarioId) {
        this.setFeedback('Ce scenario est deja actif dans l editeur.', 'info')
        return
      }
      this.selectedScenarioId = scenarioId
      this.applyScenarioPreset()
    },
    mergeScenarioBlock (currentValue, scenarioText, markerKey) {
      const startMarker = '[[' + markerKey + '_start]]'
      const endMarker = '[[' + markerKey + '_end]]'
      const cleanValue = this.stripScenarioBlock(currentValue, markerKey).trim()
      const parts = []
      if (cleanValue.length) {
        parts.push(cleanValue)
      }
      parts.push(startMarker + '\n' + scenarioText + '\n' + endMarker)
      return parts.join('\n\n').trim()
    },
    stripScenarioBlock (value, markerKey) {
      // const startMarker = '[[' + markerKey + '_start]]'
      // const endMarker = '[[' + markerKey + '_end]]'
      const pattern = new RegExp('\\n?\\[\\[' + markerKey + '_start\\]\\][\\s\\S]*?\\[\\[' + markerKey + '_end\\]\\]\\n?', 'g')
      return (value || '').replace(pattern, '\n').replace(/\n{3,}/g, '\n\n').trim()
    },
    readCsvFile (event) {
      const file = event.target && event.target.files ? event.target.files[0] : null
      if (!file) return
      const reader = new FileReader()
      reader.onload = e => {
        this.csvRawInput = e.target && e.target.result ? e.target.result : ''
      }
      reader.readAsText(file)
    },
    extractIds (rawValue) {
      return rawValue
        .split(/[\n\r,;|\t ]+/)
        .map(item => item.trim())
        .filter(Boolean)
    },
    flattenConversations (visitors) {
      const rows = []
      ;(visitors || []).forEach(visitor => {
        ;(visitor.chats || []).forEach(chat => {
          rows.push({
            conversation_id: chat.id,
            visitor_id: visitor.visId || '',
            conversation_text: this.formatConversation(visitor, chat)
          })
        })
      })
      return rows
    },
    formatConversation (visitor, chat) {
      const lines = []
      ;(chat.messages || []).forEach(msg => {
        const author = msg.auteur || 'Inconnu'
        const agentInfo = author === 'Agent' && msg.agentId ? ' id:' + msg.agentId : ''
        lines.push('[' + (msg.date || '') + '] ' + author + agentInfo + ' : ' + (msg.text || ''))
      })
      return 'Visiteur: ' + (visitor.visId || 'inconnu') + '\nConversation: ' + chat.id + '\n\n' + lines.join('\n')
    },
    getSelectedConversations () {
      const allConversations = this.flattenConversations(this.visitors)
      if (this.inputType === 'period') {
        return allConversations
      }

      const rawValue = this.inputType === 'csv' ? this.csvRawInput : this.conversationIdsInput
      const ids = this.extractIds(rawValue)
      if (!ids.length) {
        return []
      }
      return allConversations.filter(item => ids.indexOf(item.conversation_id) !== -1)
    },
    decodeParams (queryString) {
      const output = {}
      if (!queryString) return output
      queryString.split('&').forEach(part => {
        const bits = part.split('=')
        output[bits[0]] = decodeURIComponent(bits[1] || '')
      })
      return output
    },
    buildPreviousParams () {
      const decoded = this.decodeParams(this.params)
      if (!decoded.dateStart || !decoded.dateEnd) return ''
      const start = moment(decoded.dateStart)
      const end = moment(decoded.dateEnd)
      const durationMs = end.diff(start)
      const prevEnd = start.clone().subtract(1, 'seconds')
      const prevStart = prevEnd.clone().subtract(durationMs, 'milliseconds')
      decoded.dateStart = prevStart.format()
      decoded.dateEnd = prevEnd.format()
      return Object.keys(decoded).map(key => key + '=' + encodeURIComponent(decoded[key])).join('&')
    },
    async fetchPreviousPeriodConversations () {
      const prevParams = this.buildPreviousParams()
      if (!prevParams) return []
      const response = await this.axios.get('get/chats_analyse.php?' + prevParams)
      return this.flattenConversations(response.data || [])
    },
    async requestEvaluation (conversations, label) {
      const payload = new URLSearchParams()
      payload.append('sitekey', this.getSiteKey())
      payload.append('user_name', this.currentUserName())
      payload.append('input_type', this.inputType)
      payload.append('run_label', label)
      payload.append('scenario_id', this.selectedScenarioId)
      payload.append('scenario_label', this.selectedScenarioDefinition ? this.selectedScenarioDefinition.label : 'Equilibre')
      payload.append('scoring_sections', JSON.stringify(this.scoringSections))
      payload.append('conversations', JSON.stringify(conversations))
      const response = await this.axios.post('post/csat_evaluate.php', payload, {headers: {'Content-Type': 'application/x-www-form-urlencoded'}})
      return response.data
    },
    async calculateCsatScore () {
      const conversations = this.getSelectedConversations()
      if (!conversations.length) {
        this.setFeedback('Aucune conversation valide a scorer avec la selection courante.', 'warning')
        this.$emit('analysis-error', 'Aucune conversation valide a scorer avec la selection courante.')
        return
      }

      this.$emit('analysis-start')
      this.calculating = true
      this.resultSummary = null
      this.previousSummary = null
      this.resultRows = []
      this.resultErrors = []
      this.lastRunPayload = {
        inputType: this.inputType,
        ids: this.conversationIdsInput,
        csv: this.csvRawInput
      }

      try {
        const label = this.inputType === 'period' ? 'periode courante' : 'echantillon manuel'
        const currentResponse = await this.requestEvaluation(conversations, label)
        this.resultSummary = currentResponse.summary || null
        this.resultRows = currentResponse.results || []
        this.resultErrors = currentResponse.errors || []
        if (currentResponse.run) {
          this.runHistory.unshift(currentResponse.run)
          this.runHistory = this.runHistory.slice(0, 20)
        }

        let previousResponse = null
        if (this.inputType === 'period') {
          const previousConversations = await this.fetchPreviousPeriodConversations()
          if (previousConversations.length) {
            previousResponse = await this.requestEvaluation(previousConversations, 'periode precedente')
            this.previousSummary = previousResponse.summary || null
          }
        }

        this.setFeedback('Calcul CSAT termine.', 'success')
        this.$emit('analysis-result', {
          summary: this.resultSummary,
          previousSummary: this.previousSummary,
          output: {
            current: currentResponse,
            previous: previousResponse
          }
        })
      } catch (err) {
        this.setFeedback('Le calcul CSAT a echoue.', 'error')
        this.$emit('analysis-error', 'Le calcul CSAT a echoue.')
      } finally {
        this.calculating = false
      }
    },
    rerunLastCalculation () {
      if (!this.lastRunPayload) return
      this.inputType = this.lastRunPayload.inputType
      this.conversationIdsInput = this.lastRunPayload.ids
      this.csvRawInput = this.lastRunPayload.csv
      this.calculateCsatScore()
    },
    formatDateTime (value) {
      return value ? moment(value).format('DD/MM/YYYY HH:mm') : ''
    }
  }
}
</script>

<style scoped>
.tf-gap-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.csat-action-row {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.csat-inline-icon-actions {
  display: flex;
  align-items: center;
  gap: 2px;
}

.csat-icon-btn {
  opacity: 0.75;
}

.csat-icon-btn--active {
  opacity: 1;
  background: rgba(25, 118, 210, 0.12);
}

.csat-icon-btn:hover {
  opacity: 1;
}

.csat-left-topbar {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  margin-top: -4px;
  margin-bottom: 4px;
}

.csat-embedded-body {
  width: 100%;
}

.csat-icon-btn ::v-deep .v-icon {
  font-size: 20px;
}

.csat-toggle-active {
  box-shadow: 0 2px 8px rgba(25, 118, 210, 0.25);
}

.csat-left-action-area {
  min-height: 140px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.csat-left-action-area .csat-action-row {
  width: 100%;
}

.csat-soft-card {
  background: #fafbfd;
  border: 1px solid #e2e8f0;
}

.csat-analysis-dialog {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.csat-analysis-dialog-wrapper {
  max-height: 92vh;
}

.csat-analysis-dialog-wrapper .v-card {
  height: 82vh;
  min-height: 82vh;
}

.csat-dialog-header {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 64px;
  padding: 0 16px;
}

.csat-dialog-title {
  font-size: 20px;
  font-weight: 500;
}

.csat-stepper-top {
  border-bottom: 1px solid #e2e8f0;
}

.csat-dialog-content {
  flex: 1 1 auto;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: calc(82vh - 64px - 73px - 72px);
  min-height: calc(82vh - 64px - 73px - 72px);
}

.csat-step-pane {
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
}

.csat-step-pane-results {
  overflow: hidden;
}

.csat-step-pane-bot {
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
}

.csat-step-pane-body {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.csat-step-loader {
  min-height: 280px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.csat-native-textarea {
  display: block;
  width: 100%;
  height: calc(82vh - 290px);
  min-height: calc(82vh - 290px);
  box-sizing: border-box;
  resize: both;
  overflow: auto;
  border: 1px solid #8a8f98;
  border-radius: 4px;
  padding: 14px 16px;
  font-size: 14px;
  line-height: 1.45;
  color: #2b2b2b;
  background: #fff;
}

.csat-dialog-textarea ::v-deep textarea {
  font-size: 12px;
  line-height: 1.35;
}

.csat-dialog-textarea ::v-deep .v-input__slot {
  padding-top: 6px !important;
}

.csat-dialog-actions {
  padding: 12px 24px;
  border-top: 1px solid #e2e8f0;
  margin-top: auto;
}

.csat-score-card {
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  height: auto;
}

.csat-result-details-card {
  min-height: 0;
  max-width: 100%;
  overflow: hidden;
}

.csat-calculate-card {
  min-height: 100%;
}

.csat-score-chip {
  display: inline-block;
  padding: 8px 12px;
  background: #eef4ff;
  border-radius: 8px;
}

.csat-list {
  margin-top: 8px;
  padding-left: 18px;
}

.scenario-button-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.scenario-button {
  margin: 0;
}
</style>



// WEBPACK FOOTER //
// src/components/stats/CsatTrustScore.vue